import React from 'react'
import { Layout, Tooltip } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  DashboardOutlined,
  MessageOutlined,
  DatabaseOutlined,
  HistoryOutlined,
  SettingOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  ApiOutlined,
  RobotOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../stores/authStore'
import Logo from './Logo'
import './Sidebar.scss'

const { Sider } = Layout

interface SidebarProps {
  collapsed: boolean
  onCollapse: (collapsed: boolean) => void
  width?: number
  collapsedWidth?: number
  style?: React.CSSProperties
}

interface NavItem {
  key: string
  icon: React.ReactNode
  title: string
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onCollapse,
  width = 68,
  collapsedWidth = 48,
  style = {}
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['components', 'common'])
  const { user, isAdmin } = useAuthStore()

  const baseNavItems: NavItem[] = [
    { key: '/dashboard', icon: <DashboardOutlined />, title: t('navigation.dashboard') },
    { key: '/chatbot', icon: <MessageOutlined />, title: t('navigation.chatbot') },
    { key: '/knowledge', icon: <DatabaseOutlined />, title: t('navigation.knowledge') },
    { key: '/orders', icon: <ShoppingCartOutlined />, title: t('navigation.orders') },
    { key: '/history', icon: <HistoryOutlined />, title: t('navigation.history') },
  ]

  const adminNavItems: NavItem[] = isAdmin() ? [
    { key: '/agent-config', icon: <RobotOutlined />, title: t('navigation.agentConfig') },
    { key: '/humans', icon: <UserOutlined />, title: t('navigation.user_management') },
    { key: '/connectors', icon: <ApiOutlined />, title: t('navigation.connectors') },
  ] : []

  const settingsItem: NavItem[] = [
    { key: '/settings', icon: <SettingOutlined />, title: t('navigation.settings') }
  ]

  const navItems: NavItem[] = [...baseNavItems, ...adminNavItems, ...settingsItem]

  const handleItemClick = (key: string) => {
    navigate(key)
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      width={width}
      collapsedWidth={collapsedWidth}
      trigger={null}
      style={{
        overflow: 'auto',
        ...style
      }}
    >
      <div className="sidebar-logo" onClick={() => navigate('/dashboard')} title={t('app.name', { ns: 'common' })}>
        <Logo size={collapsed ? 28 : 36} />
      </div>
      <div className="sidebar-nav">
        {navItems.map((item) => {
          const isSelected = location.pathname === item.key || location.pathname.startsWith(item.key + '/')
          return (
            <div
              key={item.key}
              onClick={() => handleItemClick(item.key)}
              className={`sidebar-nav-item ${isSelected ? 'sidebar-nav-item--selected' : ''}`}
            >
              <Tooltip title={item.title} placement="right">
                <div className="sidebar-nav-icon">
                  {item.icon}
                </div>
              </Tooltip>
            </div>
          )
        })}
      </div>
      <div className="sidebar-footer">
        {!collapsed && `${t('app.fullName', { ns: 'common' })} ${t('app.version', { ns: 'common' })}`}
        {collapsed && t('app.version', { ns: 'common' })}
      </div>
    </Sider>
  )
}

export default Sidebar
